// 公开 API：AiKeChat.init({...})
//
// 支持两种模式：
//   - 生产模式（推荐）：siteKey + user{...} → SDK 调 biz-api 换 JWT → 连 WS
//   - 开发模式（本地调试）：直接传 tenantId / userId / role，跳过认证
//
// SDK 内置 token 自动刷新（过期前 5 分钟刷新；socket 关闭后重连前刷新）。

import { Client } from './client';
import { Widget } from './widget';
import { Role } from './protocol';
import { VisitorAuth, VisitorUser } from './auth';
import { Locale } from './i18n';

export type { VisitorUser as UserInfo };
export type { Locale } from './i18n';

export interface InitOptions {
  /** im-gateway 的 WS 入口；可省略，默认 `${proto}://${host}/ws` */
  serverUrl?: string;

  /** ====== 生产模式（推荐）====== */
  /** 客户 SDK 公钥；可省略 authUrl，默认 `${origin}/v1/visitor/auth` */
  siteKey?: string;
  /** 访客身份字段，全部可选；至少需要传一个有效字段（externalUserId / phone / email / deviceFp） */
  user?: VisitorUser;
  /** biz-api 的认证入口；默认走当前 origin 的 /v1/visitor/auth */
  authUrl?: string;
  /** 会话创建入口；默认走当前 origin 的 /v1/visitor/conversations */
  conversationUrl?: string;
  /** 指定技能组（一般不需要，留空走客户默认技能组） */
  skillGroupId?: string;

  /** ====== 开发模式（仅本地调试）====== */
  tenantId?: string;
  userId?: string;
  role?: Role;

  /** 已存在的会话 ID（一般留空，SDK 自动创建） */
  convId?: string;
  /** 对端 userKey；生产模式留空（由会话决定），dev 模式必填 */
  to?: string;

  /** UI 配置 */
  title?: string;
  textLimit?: number;
  /** 隐藏 SDK 自带悬浮球：由宿主网站的按钮/图标点击后调用 `AiKeChat.open()` 展开对话面板。
   *  面板与遮罩不受影响。声明式接入用 `data-hide-launcher="true"`。 */
  hideLauncher?: boolean;
  /** 显示语言（zh-CN / zh-TW / en / id / ms）；不传时按 navigator.language 自动识别 */
  locale?: Locale;

  /** ====== 身份自动跟随（可选，治「退登/换号串号」）======
   * 宿主退登时一般不 reload、也不会再调 init，SDK 收不到信号 → 坐席侧仍显示旧访客在线。
   * 声明身份来源后，SDK 会主动轮询 + 页面可见/聚焦/跨标签页时即时比对，externalUserId
   * 一变就自动切换身份（退登→游客 / 换号→新客户），宿主无需在退登处写任何代码。 */
  /** 通用回调：返回宿主当前登录用户；未登录返回 {} 或 null。SDK 会定期调用它感知身份变化，
   *  实现登录/退登/换号自动跟随。身份来源不限（内存 / cookie / 状态库 / localStorage 均可）。 */
  getUser?: () => VisitorUser | null | undefined;
}

export interface Instance {
  client: Client;
  widget: Widget;
  ready: Promise<void>;
  destroy(): void;
}

let currentInstance: Instance | null = null;

export const AiKeChat = {
  init(opts: InitOptions): Instance {
    if (currentInstance) currentInstance.destroy();

    const serverUrl = opts.serverUrl ?? defaultServerUrl();

    // 决定模式
    const isProd = !!opts.siteKey;

    // 身份来源：声明了 getUser 回调（通用，来源不限）就用它，否则用静态 opts.user。全部经
    // sanitizeUser 过滤 "null"/"undefined"/空白 —— 宿主拼错身份（如 externalUserId=null）
    // 时不会把所有访客串成一个假 customer。
    const identityProvider = resolveIdentityProvider(opts);
    const effectiveUser = identityProvider ? identityProvider() : sanitizeUser(opts.user);

    let buildConnectParams: () => Promise<Record<string, string>>;
    let userKeyPromise: Promise<string>;
    let auth: VisitorAuth | null = null;

    if (isProd) {
      if (!opts.siteKey) throw new Error('siteKey is required for production mode');
      auth = new VisitorAuth({
        authUrl: opts.authUrl ?? defaultAuthUrl(),
        siteKey: opts.siteKey,
        user: effectiveUser,
      });
      buildConnectParams = () => auth!.getConnectParams();
      userKeyPromise = auth.getUserKey();
    } else {
      const tenantId = opts.tenantId;
      const userId = opts.userId;
      const role: Role = opts.role ?? 'visitor';
      if (!tenantId || !userId) {
        throw new Error('dev mode requires tenantId and userId');
      }
      const userKey = `${tenantId}:${role === 'agent' ? 'a' : 'v'}${userId}`;
      buildConnectParams = async () => ({ tenantId, userId, role });
      userKeyPromise = Promise.resolve(userKey);
    }

    // 会话 ID：生产模式直到访客点击发送时才创建（延迟连接）；dev 模式自动生成 UUID。
    let convId = opts.convId ?? '';
    let assignedAgentKey = opts.to ?? '';

    // 刷新恢复：本地存过 convId（同浏览器曾建会话）→ 进页面即复用该会话拉历史 + 连 WS，
    // 不再走 POST /v1/visitor/conversations（GetOrCreate 语义会在旧会话已 ended 时建新进队列）。
    const storeKey = isProd ? convStoreKey(opts) : '';
    const identified = isProd && !!effectiveUser.externalUserId;
    const storedConvId = isProd ? loadStoredConvId(storeKey) : '';
    const hadStored = storedConvId !== '';
    if (hadStored) {
      convId = storedConvId;
    }

    const client = new Client({
      serverUrl,
      userKey: '', // 临时占位，下面 ready 解析后更新
      buildConnectParams,
    });

    // ensureReady：访客第一次发送时调，做"创建/复用会话 + 建立 WS + 拉历史"
    // 并发幂等：多次调用复用同一个 inflight；连接已建立后立即 resolve。
    let inflight: Promise<void> | null = null;
    const ensureReady = (): Promise<void> => {
      if (!isProd) return Promise.resolve(); // dev 模式：调用方自管
      if (inflight) return inflight;
      inflight = (async () => {
        try {
          const base = apiBaseURL(opts);
          if (!convId) {
            // 首次进线路径：访客点击发送/上传 → 现建会话 → 拉历史 → 连 WS
            const conv = await createConversation(
              opts.conversationUrl ?? defaultConversationUrl(),
              auth!,
              opts.skillGroupId,
            );
            convId = conv.id;
            // 持久化以便刷新恢复；终态则清除（下次进入开新会话）
            if (conv.state === 'ended' || conv.state === 'abandoned') {
              clearStoredConvId(storeKey);
            } else {
              storeConvId(storeKey, conv.id);
            }
            if (!assignedAgentKey && conv.agentUserKey) {
              assignedAgentKey = conv.agentUserKey;
            }
            widget.notifyConversationReady(conv);
            if (conv.rating && conv.rating > 0) widget.markRated();
            if (conv.state === 'ended' || conv.state === 'abandoned') {
              widget.markEnded();
            }
            // 当前会话自身历史（首次进线时一般为空）；失败仅警告
            try {
              const items = await fetchHistory(base, conv.id, auth!);
              widget.loadHistory(items.slice().reverse());
            } catch (herr) {
              console.warn('[leke] history fetch failed:', herr);
            }
            // 老访客（已识别）：启用「查看历史对话」折叠区，点击才按需分页加载，
            // 不再进会话即自动内联拉取近 30 天。
            if (identified) {
              widget.enableHistory((before) =>
                fetchCustomerHistory(base, conv.id, auth!, before),
              );
            }
          } else {
            // 复用路径（刷新恢复 / 已有显式 convId）：仅拉历史 + 折叠区 + 连 WS，
            // **不** POST conversations，避免无输入时 GetOrCreate 建新会话进队列。
            let staleConv = false;
            // 复用时同步坐席标识和访客昵称；在拉历史前应用名称，避免气泡显示默认「客服」。
            // 即使调用方传了 to，也需要读取最新昵称。失败不阻断进线。
            try {
              const cur = await fetchConversation(base, convId, auth!);
              if (cur.agentUserKey) assignedAgentKey = cur.agentUserKey;
              widget.notifyConversationReady(cur);
            } catch (cerr) {
              console.warn('[leke] conversation fetch failed:', cerr);
            }
            try {
              const items = await fetchHistory(base, convId, auth!);
              widget.loadHistory(items.slice().reverse());
            } catch (herr) {
              // 404 = 本地恢复的会话已不属于当前身份（典型：匿名建会话后切到登录身份，
              // 且两个身份未被合并）。弃用本地恢复点 + 清空 convId，下次发送会新建会话；
              // 这里不主动新建——resume 是无用户输入的自动触发，新建会把用户直接推进队列。
              if (isNotFoundErr(herr)) {
                clearStoredConvId(storeKey);
                convId = '';
                staleConv = true;
              } else {
                console.warn('[leke] history fetch failed:', herr);
              }
            }
            if (identified && !staleConv) {
              const reuseId = convId;
              widget.enableHistory((before) =>
                fetchCustomerHistory(base, reuseId, auth!, before),
              );
            }
          }
          if (!client.isOpen()) {
            await waitForOpen(client);
          }
        } finally {
          inflight = null;
        }
      })();
      return inflight;
    };

    // Widget 在 send 时通过 getConvId / getTo 取最新值
    const widget = new Widget({
      client,
      getConvId: () => convId,
      getTo: () => assignedAgentKey,
      ensureReady,
      textLimit: opts.textLimit,
      title: opts.title,
      locale: opts.locale,
      hideLauncher: opts.hideLauncher,
      // 上传：仅 prod 模式可用（需要 visitor JWT）
      uploadFile: isProd
        ? async (file, _kind) => {
            const token = (await auth!.getConnectParams()).token;
            const baseURL = apiBaseURL(opts);
            const presignURL = `${baseURL}/v1/visitor/attachments`;
            const pre = await fetch(presignURL, {
              method: 'POST',
              headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
              body: JSON.stringify({
                convId,
                originalName: file.name,
                mime: file.type || 'application/octet-stream',
                sizeBytes: file.size,
              }),
            }).then((r) => {
              if (!r.ok) throw new Error('presign ' + r.status);
              return r.json();
            });
            const putRes = await fetch(pre.uploadUrl, {
              method: pre.method,
              headers: pre.headers,
              body: file,
            });
            if (!putRes.ok) throw new Error('upload ' + putRes.status);
            // 通知 widget 渲染 — 返回元信息
            return {
              url: pre.downloadUrl,
              name: file.name,
              size: file.size,
              mime: file.type || 'application/octet-stream',
              attachmentId: pre.attachmentId,
            };
          }
        : undefined,
      // 访客点击"结束会话"：调 biz-api 的 visitor end 接口
      endConversation: isProd
        ? async () => {
            if (!convId) return;
            const token = (await auth!.getConnectParams()).token;
            const url = `${apiBaseURL(opts)}/v1/visitor/conversations/${convId}/end`;
            const r = await fetch(url, {
              method: 'POST',
              headers: { authorization: `Bearer ${token}` },
            });
            if (!r.ok) throw new Error('end ' + r.status);
            // 会话已结束：清除本地恢复点，下次进入开新会话（旧消息走 30 天历史）
            clearStoredConvId(storeKey);
          }
        : undefined,
      // 访客在已结束会话点"重新开始对话"：清空 convId，下一次发送会创建新会话；
      // 旧对话记录仍保留在界面上（widget 不清空）。
      restartConversation: isProd
        ? () => {
            convId = '';
            assignedAgentKey = opts.to ?? '';
            clearStoredConvId(storeKey);
          }
        : undefined,
      // 坐席结束会话（服务端下行 conv_ended）时清 stored convId，避免下次刷新
      // hadStored=true 走自动恢复路径（即便恢复路径不再 POST，stale 状态也应及时清理）。
      onAgentEnded: isProd ? () => clearStoredConvId(storeKey) : undefined,
      // 提交评分到 biz-api
      submitRating: isProd
        ? async (rating, comment) => {
            if (!convId) return;
            const token = (await auth!.getConnectParams()).token;
            const url = `${apiBaseURL(opts)}/v1/visitor/conversations/${convId}/rating`;
            const r = await fetch(url, {
              method: 'POST',
              headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
              body: JSON.stringify({ rating, comment }),
            });
            if (!r.ok) throw new Error('rating ' + r.status);
          }
        : undefined,
    });

    // ready 链路只做"取 token + 注入客户设置 + 展示欢迎语"；
    // 不再预先 client.connect() 或 createConversation，避免无效 WS 占用与 waiting 会话。
    const ready = userKeyPromise
      .then((uk) => {
        client.setUserKey(uk);
        if (auth) {
          const meta = auth.current;
          const ratingEnabled = meta?.settings?.ratingEnabled !== false;
          widget.setRatingFeatureEnabled(ratingEnabled);
          // 已读回执开关初值（缺省视为开启）;运行中由服务端 read_receipt_changed 通知实时变更
          const readReceiptEnabled = meta?.settings?.readReceiptEnabled !== false;
          widget.setReadReceiptEnabled(readReceiptEnabled);
          // 输入预览开关（缺省视为关闭：未发送内容对坐席可见，须租户显式开启）
          widget.setTypingPreviewEnabled(meta?.settings?.typingPreviewEnabled === true);
          // 客户固定语种覆盖 SDK 自动识别（仅当 SDK 调用方未显式传 locale）
          const tenantLocale = meta?.settings?.locale;
          if (!opts.locale && tenantLocale?.mode === 'fixed' && tenantLocale.fixed) {
            widget.setLocale(tenantLocale.fixed);
          }
          // 浮窗标题三级 fallback：opts.title > 服务端 widgetTitle > i18n 默认
          // 这里只处理「服务端 widgetTitle」一档；opts.title 在 Widget 构造时已生效
          const widgetTitle = meta?.settings?.widgetTitle;
          if (!opts.title && widgetTitle && widgetTitle.trim()) {
            widget.setTitle(widgetTitle);
          }
          // 品牌 logo：顶部标题前 + 坐席气泡左侧头像。须在欢迎语之前设置，
          // 否则欢迎语气泡渲染时还拿不到 logo
          widget.setLogo(meta?.settings?.logoUrl);
          // 欢迎语：进入对话页时本地立即展示（不入库、不发往服务端）
          const welcome = meta?.settings?.welcome;
          if (welcome?.enabled && welcome.message && welcome.message.trim()) {
            widget.showWelcome(welcome.message);
          }
        }
        if (!isProd && !convId) {
          // dev 模式没传 convId：本地生成一个（dev 直连）
          convId = cryptoRandomUuidV7();
        }
        if (!isProd) {
          // dev 模式仍然需要 connect（无 auth 流程）
          client.connect();
        }
        // 生产模式：本浏览器有过会话(hadStored) → 进页面即复用该会话拉历史+连 WS；
        // 已识别访客若本机无 stored，等首次发送再 createConversation，避免无输入直接进队列。
        if (isProd && hadStored) {
          void ensureReady().catch((e) => console.warn('[leke] resume failed:', e));
        }
      })
      .catch((err) => {
        console.error('[leke] init failed:', err);
      });

    // 重连补拉：WS 每次连上后补拉一次当前会话历史，补齐断连期间漏收的下行消息。
    // client 层重连只 flushPending 上行未确认帧，不回放下行——坐席在访客断线（手机休眠 /
    // 切后台 / 弱网）期间发的消息，不补拉就要等下次 ensureReady（刷新 / 再次发送）才出现，
    // 期间访客静默漏收。loadHistory 按 msgId 幂等去重：补拉只补缺、已渲染的跳过、不重渲。
    // 首个 open 时历史已由 ensureReady 的 fetchHistory 覆盖，此处再拉一次也无害（幂等）。
    const offReconnectSync = isProd
      ? client.on((e) => {
          if (e.type !== 'open' || !convId) return;
          void fetchHistory(apiBaseURL(opts), convId, auth!)
            .then((items) => widget.syncReconnectHistory(items.slice().reverse()))
            .catch((err) => console.warn('[leke] reconnect history sync failed:', err));
        })
      : () => {};

    // 跟踪会话对端（ctrl.typing 的 to）。assignedAgentKey 原先只在「新建会话且当时已有坐席」
    // 那一条路径上赋值一次，于是三种情况下访客永远发不出输入指示：排队时进线、刷新恢复会话、
    // 会话被转接（to 还指着原坐席）。这里补上后两条的实时来源：
    //   - 转接：conv_transferred 直接带新坐席 userKey，无需回拉；
    //   - 坐席下行消息的 frame.from 由 gateway 按连接身份覆写、伪造不了，直接采信；
    //   - 脱队（queue_position 的 position=0）意味着刚被接入，但该帧不带坐席标识，只能回拉一次。
    const offAgentKeyTrack = isProd
      ? client.on((e) => {
          if (e.type === 'message') {
            const from = e.frame.from ?? '';
            const i = from.indexOf(':');
            if (i > 0 && from[i + 1] === 'a' && from !== assignedAgentKey) {
              assignedAgentKey = from;
            }
            return;
          }
          if (e.type !== 'notice' || !convId) return;
          if (e.payload.code === 'conv_transferred') {
            if (e.payload.agentUserKey) assignedAgentKey = e.payload.agentUserKey;
            return;
          }
          if (e.payload.code !== 'queue_position' || (e.payload.position ?? 0) !== 0) return;
          void fetchConversation(apiBaseURL(opts), convId, auth!)
            .then((cur) => {
              if (cur.agentUserKey) assignedAgentKey = cur.agentUserKey;
              widget.notifyConversationReady(cur);
            })
            .catch((err) => console.warn('[leke] conversation fetch failed:', err));
        })
      : () => {};

    // 页面恢复探活：切回标签页 / 窗口聚焦 / 后台解冻 / 网络恢复时，主动让 client 探活重连。
    // 半死连接（readyState 仍 OPEN 但 TCP 已断、onclose 不触发）靠这里 + client 看门狗兜底；
    // 前台一直开着不操作的掉线由 client 看门狗覆盖，无需页面事件。ensureAlive 内部只在「曾连过」
    // 时才动作，故未发消息的游客不会因切页面被强行建连。
    let offAliveProbe: (() => void) | null = null;
    if (isProd) {
      const probe = () => {
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
        client.ensureAlive();
      };
      const onlineProbe = () => client.ensureAlive(); // 网络恢复即使页面在后台也探
      if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', probe);
        document.addEventListener('resume', probe); // Page Lifecycle 解冻
      }
      if (typeof window !== 'undefined') {
        window.addEventListener('focus', probe);
        window.addEventListener('online', onlineProbe);
      }
      offAliveProbe = () => {
        if (typeof document !== 'undefined') {
          document.removeEventListener('visibilitychange', probe);
          document.removeEventListener('resume', probe);
        }
        if (typeof window !== 'undefined') {
          window.removeEventListener('focus', probe);
          window.removeEventListener('online', onlineProbe);
        }
      };
    }

    // 身份自动跟随：声明了 getUser 回调时，SDK 主动轮询 +
    // 页面可见/聚焦/跨标签页 storage 事件即时比对；externalUserId 变化即用新身份重跑 init
    // （内部 destroy 当前实例、含本 watcher）：有值→空=退登→游客，A→B=换号，空→有值=登录。
    // 宿主退登处无需写任何代码。指纹只看 external/phone/email，不含 deviceFp（避免兜底指纹误判）。
    let identityWatchTimer: number | null = null;
    let onIdentityWake: (() => void) | null = null;
    if (isProd && identityProvider) {
      let lastFp = identityFingerprint(effectiveUser);
      const check = () => {
        let next: VisitorUser;
        try {
          next = identityProvider();
        } catch {
          return;
        }
        const fp = identityFingerprint(next);
        if (fp === lastFp) return;
        lastFp = fp; // 先更新基线，避免 re-init 前重复触发
        AiKeChat.init(opts); // provider 会读到新身份重建；不一致才走到这里，无循环
      };
      identityWatchTimer = window.setInterval(check, 3000);
      onIdentityWake = () => {
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
        check();
      };
      if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', onIdentityWake);
      }
      if (typeof window !== 'undefined') {
        window.addEventListener('focus', onIdentityWake);
        window.addEventListener('storage', onIdentityWake);
      }
    }

    currentInstance = {
      client,
      widget,
      ready,
      destroy: () => {
        offAliveProbe?.();
        if (identityWatchTimer != null) clearInterval(identityWatchTimer);
        if (onIdentityWake) {
          if (typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', onIdentityWake);
          }
          if (typeof window !== 'undefined') {
            window.removeEventListener('focus', onIdentityWake);
            window.removeEventListener('storage', onIdentityWake);
          }
        }
        offReconnectSync();
        offAgentKeyTrack();
        widget.destroy();
        client.close();
        currentInstance = null;
      },
    };
    return currentInstance;
  },

  /** 展开对话面板。供宿主网站用自己的按钮/图标触发（如 <button onclick="AiKeChat.open()">）。
   *  须先 init 过；未 init 时静默忽略。首次进线仍是延迟连接：真正建会话进队列要等访客首次发送消息。 */
  open(): void {
    currentInstance?.widget.openPanel();
  },

  /** 收起对话面板。 */
  close(): void {
    currentInstance?.widget.closePanel();
  },
};

// sdkOrigin 优先级：
//   1. window.__LEKE_HOST__（platform 后台注入的强制覆盖）
//   2. document.currentScript.src 的 origin（脚本所在 host，即 SDK 入口）
//   3. location.origin 兜底（与 SDK 同源时正确；跨域时多半错的）
//
// 这样客户端嵌入只需要一行 <script src="https://cdn.aikechat.cc/sdk/leke-chat.umd.js">,
// SDK 自动得知 biz-api 在脚本所在 host（CDN 同源代理 /v1 与 /ws），无需显式传 serverUrl/authUrl/conversationUrl。
function sdkOrigin(): string {
  if (typeof window !== 'undefined') {
    const forced = (window as unknown as { __LEKE_HOST__?: string }).__LEKE_HOST__;
    if (typeof forced === 'string' && forced) {
      try {
        return new URL(forced).origin;
      } catch {
        /* ignore */
      }
    }
  }
  if (typeof document !== 'undefined') {
    const cur = (document as Document & { currentScript?: HTMLScriptElement | null }).currentScript;
    if (cur && cur.src) {
      try {
        return new URL(cur.src).origin;
      } catch {
        /* ignore */
      }
    }
    // currentScript 在异步加载时为 null。从已有 <script> 兜底找最后一个含 leke-chat 的脚本。
    const scripts = document.getElementsByTagName('script');
    for (let i = scripts.length - 1; i >= 0; i--) {
      const src = scripts[i].src;
      if (src && /leke-chat/i.test(src)) {
        try {
          return new URL(src).origin;
        } catch {
          /* ignore */
        }
      }
    }
  }
  if (typeof location !== 'undefined') return location.origin;
  return 'http://localhost:18080';
}

function defaultServerUrl(): string {
  const origin = sdkOrigin();
  const proto = origin.startsWith('https:') ? 'wss:' : 'ws:';
  const host = origin.replace(/^https?:/, '');
  return `${proto}${host}/ws`;
}

function defaultAuthUrl(): string {
  return `${sdkOrigin()}/v1/visitor/auth`;
}

function defaultConversationUrl(): string {
  return `${sdkOrigin()}/v1/visitor/conversations`;
}

// ===== 访客身份净化与自动跟随 =====

// 宿主拼错 / 未替换的假身份字面量（如 externalUserId=null）。当真会把所有访客串成一个
// 假 customer；统一当空处理，退化为 deviceFp 游客。
const BAD_ID_LITERALS = new Set(['null', 'undefined', 'nil', 'none', 'nan', '(null)']);

function cleanId(v: unknown): string | undefined {
  if (v == null) return undefined;
  const s = String(v).trim();
  if (!s) return undefined;
  if (BAD_ID_LITERALS.has(s.toLowerCase())) return undefined;
  return s;
}

// sanitizeUser 过滤身份字段里的假字面量 / 空白；username 仅去空白（显示名，不参与身份匹配）。
function sanitizeUser(u?: VisitorUser | null): VisitorUser {
  if (!u) return {};
  const out: VisitorUser = {};
  const eu = cleanId(u.externalUserId);
  if (eu) out.externalUserId = eu;
  const ph = cleanId(u.phone);
  if (ph) out.phone = ph;
  const em = cleanId(u.email);
  if (em) out.email = em;
  const fp = cleanId(u.deviceFp);
  if (fp) out.deviceFp = fp;
  if (u.username != null && String(u.username).trim()) out.username = String(u.username).trim();
  if (u.extra) out.extra = u.extra;
  return out;
}

// identityFingerprint 供身份 watcher 比对「人是否变了」。只取 external/phone/email：
// deviceFp 是设备兜底、VisitorAuth 会自动补，纳入指纹会让游客态每次比对都"变化"。
function identityFingerprint(u: VisitorUser): string {
  const s = sanitizeUser(u);
  return [s.externalUserId ?? '', s.phone ?? '', s.email ?? ''].join('');
}

// resolveIdentityProvider：声明了 getUser 回调（通用，来源不限）时返回一个「每次重新读取宿主
// 当前身份并净化」的函数；未声明则返回 null（不启用自动跟随）。回调偶发抛错退化为游客。
function resolveIdentityProvider(opts: InitOptions): (() => VisitorUser) | null {
  if (opts.getUser) {
    const g = opts.getUser;
    return () => {
      try {
        return sanitizeUser(g());
      } catch {
        // 宿主回调偶发抛错：退化为游客，别拖垮 init / watcher
        return {};
      }
    };
  }
  return null;
}

interface ConversationCreateResp {
  id: string;
  state: string;
  agentId?: string;
  agentUserKey?: string;
  agentName?: string;
  customerUserKey: string;
  startedAt: string;
  rating?: number;
  /** 仅当 state='waiting' 时返回，访客在队列里的位置（1-based） + 队列总长度 */
  queue?: { position: number; total: number } | null;
}

// 访客浏览器 IANA 时区（如 Asia/Shanghai），供坐席工作台显示访客当地时间。
// 取不到（极老环境）时返回 undefined，后端按空处理。
function visitorTimezone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

async function createConversation(
  url: string,
  auth: VisitorAuth,
  skillGroupId?: string,
): Promise<ConversationCreateResp> {
  const params = await auth.getConnectParams();
  const token = params.token;
  const resp = await fetch(url, {
    method: 'POST',
    mode: 'cors',
    credentials: 'omit',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      sourceUrl: typeof location !== 'undefined' ? location.href : undefined,
      referer: typeof document !== 'undefined' ? document.referrer : undefined,
      timezone: visitorTimezone(),
      skillGroupId,
    }),
  });
  // 200 / 201 / 202（202 = 暂无可用坐席仍创建成功）
  if (resp.status >= 400) {
    let body: { code?: string; message?: string } | null = null;
    try { body = await resp.json() as { code?: string; message?: string }; } catch { /* ignore */ }
    throw new Error(
      `conversation create failed [${body?.code ?? 'http_' + resp.status}]: ${body?.message ?? resp.statusText}`,
    );
  }
  const raw = await resp.json();
  const conv = raw.conversation as ConversationCreateResp;
  if (raw.queue) {
    conv.queue = raw.queue as { position: number; total: number };
  }
  return conv;
}

// apiBaseURL 取 biz-api 的 origin：
//   1) opts.authUrl 显式传入时用它的 origin（CORS 域）
//   2) 否则走 sdkOrigin —— SDK 脚本的来源 host，跟 defaultAuthUrl/defaultConversationUrl 一致
// 千万不能 fallback 到 location.origin：SDK 嵌在客户网站时 location.origin 是客户域名，
// POST /v1/visitor/conversations/:id/end 会打到客户自己的 server 上返 405。
function apiBaseURL(opts: InitOptions): string {
  if (opts.authUrl) return new URL(opts.authUrl).origin;
  return sdkOrigin();
}

interface HistoryRespItem {
  id: string;
  senderType: 'visitor' | 'agent' | 'bot' | 'system';
  contentType: string;
  content: unknown;
  recalled?: boolean;
  createdAt: string;
  convId?: string;
}

// fetchHistory / fetchCustomerHistory 失败时抛 `Error('history 404')` 这类消息，
// 用它判断是否「会话不属于当前身份」（HTTP 404）。
function isNotFoundErr(err: unknown): boolean {
  return err instanceof Error && /\b404\b/.test(err.message);
}

// fetchConversation 取当前会话的只读快照，同步坐席 userKey 与访客昵称。
// 不用 POST /v1/visitor/conversations：那是 GetOrCreate，无访客输入时调会把人推进队列。
async function fetchConversation(
  baseURL: string,
  convId: string,
  auth: VisitorAuth,
): Promise<ConversationCreateResp> {
  const params = await auth.getConnectParams();
  const r = await fetch(`${baseURL}/v1/visitor/conversations/${convId}`, {
    method: 'GET',
    headers: { authorization: `Bearer ${params.token}` },
  });
  if (!r.ok) throw new Error('conversation ' + r.status);
  return (await r.json()) as ConversationCreateResp;
}

async function fetchHistory(
  baseURL: string,
  convId: string,
  auth: VisitorAuth,
): Promise<HistoryRespItem[]> {
  const params = await auth.getConnectParams();
  const r = await fetch(`${baseURL}/v1/visitor/conversations/${convId}/messages?limit=50`, {
    method: 'GET',
    headers: { authorization: `Bearer ${params.token}` },
  });
  if (!r.ok) throw new Error('history ' + r.status);
  const body = (await r.json()) as { items: HistoryRespItem[] };
  return body.items ?? [];
}

// 跨会话历史对话（同源站ID/customer）：before 游标分页、不限时间。
// 后端按时间倒序返回 + hasMore；折叠区点击展开 / 加载更多时按需调用。
async function fetchCustomerHistory(
  baseURL: string,
  convId: string,
  auth: VisitorAuth,
  before?: string,
): Promise<{ items: HistoryRespItem[]; hasMore: boolean }> {
  const params = await auth.getConnectParams();
  const qs = new URLSearchParams({ limit: '30' });
  if (before) qs.set('before', before);
  const r = await fetch(
    `${baseURL}/v1/visitor/conversations/${convId}/history?${qs.toString()}`,
    { method: 'GET', headers: { authorization: `Bearer ${params.token}` } },
  );
  if (!r.ok) throw new Error('cust-history ' + r.status);
  const body = (await r.json()) as { items: HistoryRespItem[]; hasMore?: boolean };
  return { items: body.items ?? [], hasMore: !!body.hasMore };
}

// 会话 ID 本地持久化：访客刷新后凭此恢复未结束会话。按 siteKey 隔离多站点。
function convStoreKey(opts: InitOptions): string {
  return 'leke.conv:' + (opts.siteKey || 'dev');
}
function loadStoredConvId(key: string): string {
  try {
    return (typeof localStorage !== 'undefined' && localStorage.getItem(key)) || '';
  } catch {
    return '';
  }
}
function storeConvId(key: string, id: string): void {
  try {
    localStorage.setItem(key, id);
  } catch {
    /* 隐私模式禁用 storage：忽略，退化为不可跨刷新恢复 */
  }
}
function clearStoredConvId(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/**
 * waitForOpen 触发 client.connect() 并 await 第一次 'open' 事件。
 * 超时 8s 抛错——避免 ensureReady 永久挂起。
 */
function waitForOpen(client: Client): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (client.isOpen()) {
      resolve();
      return;
    }
    let settled = false;
    const off = client.on((e) => {
      if (settled) return;
      if (e.type === 'open') {
        settled = true;
        off();
        resolve();
      }
    });
    const timer = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      off();
      reject(new Error('connect timeout'));
    }, 8000);
    // 注册完 listener 再启动 connect，避免极快路径下漏 open 事件
    client.connect();
    void timer;
  });
}

function cryptoRandomUuidV7(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x70;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const h = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

declare global {
  interface Window {
    AiKeChat?: typeof AiKeChat;
  }
}
// 已知的中文占位文案：admin 复制嵌入代码后忘记替换，常见的几个原样字符串
// 会被工作台当成真实访客 ID 显示，必须在 SDK 入口拦掉。
// ⚠ 必须声明在下面 `autoInit()` 立即调用之前，否则会落入 TDZ 抛 ReferenceError。
const PLACEHOLDER_LITERALS = new Set([
  '源站ID',
  '源站 ID',
  '源站用户ID',
  '源站用户 ID',
  '你的源站ID',
  '您的源站ID',
  '你的源站用户ID',
  '您的源站用户ID',
  '你的网站用户ID',
  '您的网站用户ID',
  '用户姓名',
  '在线客服',
]);

if (typeof window !== 'undefined') {
  window.AiKeChat = AiKeChat;
  // 加载完毕后尝试一次"零代码"自动 init：读 script 标签上的 data-* 属性；
  // data-auto-init="false" 时跳过。
  autoInit();
}

/**
 * autoInit 让客户**完全不用写 JS** 就能接入：
 *
 *   <script src=".../leke-chat.umd.js"
 *           data-site-key="..."
 *           data-external-user-id="..."
 *           data-username="...">
 *   </script>
 *
 * 没有任何 data-* 配置时静默退出，改用编程式 `AiKeChat.init({...})`。
 * 需要传 user.extra 这种复杂结构、或用函数式 getUser 时，也走 `AiKeChat.init({...})`。
 */
function autoInit(): void {
  try {
    const sdkScript = findSelfScript();
    const rawDs = (sdkScript?.dataset ?? {}) as Record<string, string | undefined>;
    // 统一 trim + 占位过滤：未替换的中文占位文案（如 data-external-user-id="源站ID"）
    // 不要当真值塞给后端，否则工作台会出现一堆叫"源站ID"的访客。
    const ds: Record<string, string | undefined> = {};
    for (const k of Object.keys(rawDs)) {
      const v = sanitizeDataAttr(k, rawDs[k]);
      if (v !== undefined) ds[k] = v;
    }
    if (rawDs.autoInit === 'false') return;

    const siteKey = ds.siteKey;
    const tenantId = ds.tenantId;
    // 既无 siteKey 也无 tenantId → 视为"手动模式"（改用 AiKeChat.init()），不自动 init
    if (!siteKey && !tenantId) return;

    const merged: InitOptions = {
      ...(siteKey ? { siteKey } : {}),
      ...(ds.serverUrl ? { serverUrl: ds.serverUrl } : {}),
      ...(ds.authUrl ? { authUrl: ds.authUrl } : {}),
      ...(ds.conversationUrl ? { conversationUrl: ds.conversationUrl } : {}),
      ...(ds.skillGroupId ? { skillGroupId: ds.skillGroupId } : {}),
      ...(ds.convId ? { convId: ds.convId } : {}),
      ...(ds.to ? { to: ds.to } : {}),
      ...(ds.title ? { title: ds.title } : {}),
      ...(ds.textLimit ? { textLimit: parseInt(ds.textLimit, 10) } : {}),
      ...(rawDs.hideLauncher === 'true' ? { hideLauncher: true } : {}),
      ...(ds.locale ? { locale: ds.locale as Locale } : {}),
      ...(tenantId ? { tenantId } : {}),
      ...(ds.userId ? { userId: ds.userId } : {}),
      ...(ds.role ? { role: ds.role as Role } : {}),
    };
    // user 字段：data-* 扁平字符串组装
    const userFromData: VisitorUser = {};
    if (ds.externalUserId) userFromData.externalUserId = ds.externalUserId;
    if (ds.username) userFromData.username = ds.username;
    if (ds.phone) userFromData.phone = ds.phone;
    if (ds.email) userFromData.email = ds.email;
    if (ds.deviceFp) userFromData.deviceFp = ds.deviceFp;
    if (Object.keys(userFromData).length > 0) {
      merged.user = userFromData;
    }

    // data-callback：声明式接入下的「动态身份回调」，值是宿主全局函数名（如 data-callback="getUser"）。
    // 完全显式：必须写了函数名、且该全局函数此刻已存在，才启用——留空 / 不写 / 函数不存在
    // 一律不处理（回退静态 user / 游客）。这样绝不去猜、绝不误调宿主无关的同名方法。
    // 注意函数须在本 SDK 脚本之前定义；启用后每次调用都从 window 重新取，支持运行时替换。
    const cbName = rawDs.callback?.trim();
    if (cbName && typeof (window as unknown as Record<string, unknown>)[cbName] === 'function') {
      merged.getUser = () => {
        const f = (window as unknown as Record<string, unknown>)[cbName];
        return typeof f === 'function' ? (f as () => VisitorUser | null | undefined)() : {};
      };
    }

    AiKeChat.init(merged);
  } catch (err) {
    console.warn('[leke] auto-init failed:', err);
  }
}

function sanitizeDataAttr(key: string, raw: string | undefined): string | undefined {
  if (raw == null) return undefined;
  const v = raw.trim();
  if (!v) return undefined;
  // <...> 风格占位永远跳过（snippet 模板推荐用 <您的源站用户ID> 这种语法）
  if (v.startsWith('<') && v.endsWith('>')) {
    console.warn(`[leke] data-${key} looks like a placeholder "${v}" and was ignored`);
    return undefined;
  }
  if (PLACEHOLDER_LITERALS.has(v)) {
    console.warn(`[leke] data-${key} is an unreplaced placeholder value "${v}" and was ignored`);
    return undefined;
  }
  return v;
}

/**
 * findSelfScript 找到加载本 SDK 的 <script> 标签，用于读取 data-* 属性。
 * 优先 document.currentScript（同步加载时可用），fallback 遍历查找 src 含 leke-chat 的 script。
 */
function findSelfScript(): HTMLScriptElement | null {
  if (typeof document === 'undefined') return null;
  const cur = (document as Document & { currentScript?: HTMLScriptElement | null }).currentScript;
  if (cur && cur.tagName === 'SCRIPT') return cur;
  const scripts = document.getElementsByTagName('script');
  for (let i = scripts.length - 1; i >= 0; i--) {
    if (scripts[i].src && /leke-chat/i.test(scripts[i].src)) return scripts[i];
  }
  return null;
}

export default AiKeChat;
